import React, { useCallback, useMemo, useState } from 'react';
import type { AddMedicationRequest, UpdateMedicationRequest } from '@features/patients';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Box, Button, Chip, IconButton, useTheme } from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridDensity,
  type GridRowId,
  type GridRowModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  useGridApiRef,
} from '@mui/x-data-grid';
import { useEventTracking } from '@services/statsig';

import type { Medication } from '../types';

interface PatientMedicationsEditCardProps {
  medications: Medication[];
  onAddMedication: (request: AddMedicationRequest) => Promise<Medication>;
  onUpdateMedication: (medicationId: string, patch: UpdateMedicationRequest) => Promise<void>;
  onDeleteMedication: (medicationId: string) => Promise<void>;
  isLoading?: boolean;
  patientId?: string;
  /** Source identifier for analytics */
  source?: string;
}

interface CustomToolbarProps {
  onAddRow: () => void;
  isLoading: boolean;
  buttonLabel: string;
}

const CustomToolbar: React.FC<CustomToolbarProps> = ({ onAddRow, isLoading, buttonLabel }) => {
  return (
    <GridToolbarContainer
      sx={{
        mb: 3,
        gap: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        '& .MuiButton-root': {
          borderRadius: '20px',
          textTransform: 'none',
          fontWeight: 500,
        },
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <GridToolbarColumnsButton />
        <GridToolbarDensitySelector />
      </Box>
      <Button
        variant="contained"
        size="small"
        startIcon={<AddIcon />}
        onClick={onAddRow}
        disabled={isLoading}
        sx={{
          borderRadius: '999px',
          textTransform: 'none',
          fontWeight: 500,
          px: 3,
          bgcolor: 'primary.main',
          '&:hover': {
            bgcolor: 'primary.dark',
          },
          '&:disabled': {
            bgcolor: 'action.disabledBackground',
            color: 'action.disabled',
          },
        }}
      >
        {buttonLabel}
      </Button>
    </GridToolbarContainer>
  );
};

const formatDate = (date: string | null | undefined): string => {
  if (!date) return '';
  return date.split('T')[0];
};

const toIsoDate = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === 'string') return value.split('T')[0];
  if (value instanceof Date) return value.toISOString().split('T')[0];
  return null;
};

const ALWAYS_VISIBLE_MEDICATION_FIELDS = ['name', 'is_active', 'actions'];

export const PatientMedicationsEditCard: React.FC<PatientMedicationsEditCardProps> = ({
  medications,
  onAddMedication,
  onUpdateMedication,
  onDeleteMedication,
  isLoading = false,
  patientId,
  source = 'context_bar',
}) => {
  const theme = useTheme();
  const [density, setDensity] = useState<GridDensity>('compact');
  const apiRef = useGridApiRef();
  const { patient: patientTracking } = useEventTracking();

  const initialColumnVisibilityModel = useMemo(() => {
    const visibilityModel: GridColumnVisibilityModel = {};
    const fields = [
      'name',
      'generic_name',
      'form',
      'strength',
      'route',
      'dosage',
      'frequency',
      'timing',
      'start_date',
      'end_date',
      'indication',
      'is_active',
      'actions',
    ];

    fields.forEach(field => {
      if (ALWAYS_VISIBLE_MEDICATION_FIELDS.includes(field)) {
        visibilityModel[field] = true;
        return;
      }

      if (medications.length === 0) {
        visibilityModel[field] = true;
        return;
      }

      const hasValue = medications.some(med => {
        const value = med[field as keyof Medication];
        return value !== null && value !== undefined && value !== '';
      });
      visibilityModel[field] = hasValue;
    });

    return visibilityModel;
  }, [medications]);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
    initialColumnVisibilityModel,
  );

  const handleDeleteClick = useCallback(
    async (id: GridRowId) => {
      await onDeleteMedication(id as string);
      if (patientId) {
        patientTracking.updated(patientId, {
          source,
          fields_updated: ['medications_item_deleted'],
        });
      }
    },
    [onDeleteMedication, patientId, patientTracking, source],
  );

  const processRowUpdate = useCallback(
    async (newRow: GridRowModel, oldRow: GridRowModel) => {
      const medicationId = newRow.medication_id as string;
      const patch: UpdateMedicationRequest = {
        name: newRow.name ?? null,
        generic_name: newRow.generic_name ?? null,
        form: newRow.form ?? null,
        strength: newRow.strength ?? null,
        route: newRow.route ?? null,
        dosage: newRow.dosage ?? null,
        frequency: newRow.frequency ?? null,
        timing: newRow.timing ?? null,
        start_date: toIsoDate(newRow.start_date),
        end_date: toIsoDate(newRow.end_date),
        indication: newRow.indication ?? null,
        is_active: newRow.is_active ?? null,
      };

      try {
        await onUpdateMedication(medicationId, patch);
        if (patientId) {
          patientTracking.updated(patientId, {
            source,
            fields_updated: ['medications_item_edited'],
          });
        }
        return newRow;
      } catch {
        return oldRow;
      }
    },
    [onUpdateMedication, patientId, patientTracking, source],
  );

  const handleAddRow = useCallback(async () => {
    const defaultName = `New Medication ${medications.length + 1}`;
    const created = await onAddMedication({ name: defaultName, is_active: true });

    setTimeout(() => {
      if (apiRef.current && created.medication_id) {
        apiRef.current.startCellEditMode({
          id: created.medication_id,
          field: 'name',
        });
      }
    }, 100);

    if (patientId) {
      patientTracking.updated(patientId, {
        source: 'context_bar',
        fields_updated: ['medications_item_added'],
      });
    }
  }, [medications.length, onAddMedication, apiRef, patientId, patientTracking]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Medication',
        minWidth: 150,
        flex: 1.5,
        editable: true,
      },
      {
        field: 'generic_name',
        headerName: 'Generic',
        minWidth: 120,
        flex: 1,
        editable: true,
      },
      {
        field: 'form',
        headerName: 'Form',
        minWidth: 80,
        flex: 0.7,
        editable: true,
      },
      {
        field: 'strength',
        headerName: 'Strength',
        minWidth: 80,
        flex: 0.7,
        editable: true,
      },
      {
        field: 'route',
        headerName: 'Route',
        minWidth: 60,
        flex: 0.6,
        editable: true,
      },
      {
        field: 'dosage',
        headerName: 'Dosage',
        minWidth: 80,
        flex: 0.7,
        editable: true,
      },
      {
        field: 'frequency',
        headerName: 'Frequency',
        minWidth: 100,
        flex: 0.8,
        editable: true,
      },
      {
        field: 'timing',
        headerName: 'Timing',
        minWidth: 80,
        flex: 0.7,
        editable: true,
      },
      {
        field: 'start_date',
        headerName: 'Start Date',
        flex: 1,
        editable: true,
        type: 'date',
        valueGetter: (_, row) => {
          const value = row.start_date;
          return value ? new Date(value) : null;
        },
        valueFormatter: (_, row) => formatDate(row.start_date),
      },
      {
        field: 'end_date',
        headerName: 'End Date',
        flex: 1,
        editable: true,
        type: 'date',
        valueGetter: (_, row) => {
          const value = row.end_date;
          return value ? new Date(value) : null;
        },
        valueFormatter: (_, row) => formatDate(row.end_date),
      },
      {
        field: 'indication',
        headerName: 'Indication',
        flex: 2,
        editable: true,
      },
      {
        field: 'is_active',
        headerName: 'Status',
        flex: 1,
        editable: true,
        type: 'boolean',
        renderCell: params => {
          const isActive = params.value !== false;
          return (
            <Chip
              label={isActive ? 'Active' : 'Inactive'}
              size="small"
              sx={{
                borderRadius: '999px',
                bgcolor: isActive ? 'rgba(103, 80, 164, 0.12)' : 'rgba(121, 116, 126, 0.12)',
                color: isActive ? 'rgba(103, 80, 164, 1)' : 'rgba(121, 116, 126, 1)',
                fontWeight: 500,
                border: 'none',
              }}
            />
          );
        },
      },
      {
        field: 'actions',
        headerName: '',
        width: 50,
        sortable: false,
        filterable: false,
        editable: false,
        renderCell: params => (
          <IconButton
            size="small"
            aria-label="Delete medication"
            onClick={e => {
              e.stopPropagation();
              void handleDeleteClick(params.id);
            }}
            sx={{
              color: 'grey.500',
              '&:hover': {
                color: 'error.main',
              },
            }}
          >
            <RemoveCircleOutlineIcon fontSize="small" />
          </IconButton>
        ),
      },
    ],
    [handleDeleteClick],
  );

  const rows = useMemo(() => {
    return medications.map(med => ({ ...med, id: med.medication_id }));
  }, [medications]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <DataGrid
          apiRef={apiRef}
          rows={rows}
          columns={columns}
          density={density}
          onDensityChange={newDensity => setDensity(newDensity)}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          disableRowSelectionOnClick
          processRowUpdate={processRowUpdate}
          hideFooter
          slots={{
            toolbar: () => (
              <CustomToolbar
                onAddRow={() => void handleAddRow()}
                isLoading={isLoading}
                buttonLabel="Add Medication"
              />
            ),
          }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: 'action.hover',
              borderRadius: '12px',
              fontWeight: 600,
              borderBottom: 'none',
            },
            '& .MuiDataGrid-row': {
              borderRadius: '8px',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            },
            '& .MuiDataGrid-cell': {
              borderBottom: 'none',
            },
          }}
        />
      </Box>
    </Box>
  );
};
